// useAutoOpenFirstFeatureWindow — "select a feature → its first parameter
// window opens" (the JAWS feel, generalized to every domain).
//
// Reacts to the RESOLVED surface key changing (from featureWindowSurface.ts),
// regardless of what UI interaction caused it — a sub-tab pill click, a
// primitive picked from a toolbar dropdown, browser Back/Forward, whatever.
// This replaces piecemeal per-click wiring (which previously only covered the
// plain sub-tab pills, missing the compound Implicit-Primitives/Wheels/Heat-
// Exchanger dropdowns entirely) with one place that's provably correct for
// every surface.
//
// Mount ONCE, high in the tree (FeatureTreeSidebar) — it has no visual output.

import { useEffect, useRef } from 'react';
import {
  useFeatureWindows,
  resetFeatureWindows,
} from '@/components/viewer/featureWindow/useFeatureWindows';
import type { FeatureWindowItem } from './featureWindowRegistry';

export function useAutoOpenFirstFeatureWindow(
  surfaceKey: string | null,
  items: FeatureWindowItem[],
): void {
  const { openFeature } = useFeatureWindows();
  const prevKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (surfaceKey === prevKeyRef.current) return;
    prevKeyRef.current = surfaceKey;
    resetFeatureWindows();
    if (surfaceKey && items.length > 0) {
      openFeature(items[0].key);
    }
    // items is derived from surfaceKey (same registry lookup) — re-running on
    // its identity would fight the "only react to a NEW surface" contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surfaceKey, openFeature]);
}
